import {
  Button,
  CustomFilter,
  EllipsisSpan,
  FieldTable,
  Form,
  FormContent,
  Input,
  Paging,
  PopConfirm,
  useAsync,
  useBreadcrumbRoutes,
  usePaging,
  useUpdate,
  Wrapper,
} from '@maxtropy/components';
import { Cascader, Space } from 'antd';
import { useRequest } from 'ahooks';
import { Key, useMemo, useState } from 'react';
import { apiV2AreaDeviceRelationRemovePost, apiV2AreaDeviceRelationPagePost } from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import UnitModal from './component/UnitModal';
import { PlusOutlined } from '@ant-design/icons';
import { Device } from '@/api/device';
import { adaptToDeviceTypeTree, DeviceTypeTree, formatOptionData, getChildNodesByParentIds } from './utils';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import StatisticalDataModal from './component/StatisticalDataModal';
import CheckDataProperty from './component/CheckDataProperty';
import { useParams } from 'react-router-dom';
import { getDeviceTypeTree } from '@/api/deviceType';
import styles from './index.module.scss';
dayjs.extend(customParseFormat);

export type DeviceListItem = Partial<Omit<Device, 'rootCode' | 'deviceId'>>;

interface Params {
  codeOrName?: string;
  typeId?: number[];
}

const columns = [
  {
    title: '设备编号',
    dataIndex: 'deviceCode',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'deviceName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

const LinkedMeteringDevices = () => {
  const { id } = useParams();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const pagingInfo = usePaging(10);
  const [update, updateFn] = useUpdate();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [showModal, setShowModal] = useState(false);
  const [tableSelectedRowKeys, setTableSelectedRowKeys] = useState<Key[]>([]);
  const [batchRemoveMode, setBatchRemoveMode] = useState(false);
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState<Params>();
  const [statisticalModalVisible, setStatisticalModalVisible] = useState<boolean>(false);
  const [checkDataPropertyVisible, setCheckDataPropertyVisible] = useState<boolean>(false);
  const [currentDeviceId, setCurrentDeviceId] = useState<number>();

  // 获取设备列表数据
  const { data: deviceData, loading } = useRequest(
    () =>
      apiV2AreaDeviceRelationPagePost({
        areaId: Number(id),
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res?.total ?? 0);
        return res.list || [];
      }),
    {
      refreshDeps: [pageOffset, update, pageSize, searchParams],
    }
  );

  //获取所属类目数据
  const rawData = useAsync(getDeviceTypeTree);
  const deviceTypeData = useMemo<DeviceTypeTree | undefined>(() => {
    return rawData ? adaptToDeviceTypeTree(rawData) : undefined;
  }, [rawData]);

  const deleteConfig = (deviceIds: number[] | number) => {
    const ids = Array.isArray(deviceIds) ? deviceIds : [deviceIds];
    apiV2AreaDeviceRelationRemovePost({
      areaId: Number(id),
      deviceIds: ids,
    }).then(() => {
      updateFn();
    });
  };

  // 表格行选择变化
  const onTableSelectChange = (keys: Key[]) => {
    setTableSelectedRowKeys(keys);
  };
  const rowSelection = batchRemoveMode
    ? {
        selectedRowKeys: tableSelectedRowKeys,
        onChange: onTableSelectChange,
      }
    : undefined;

  // 批量移除模式切换
  const toggleBatchRemoveMode = () => {
    if (batchRemoveMode) {
      // 退出批量移除模式时清空选择
      setTableSelectedRowKeys([]);
    }
    setBatchRemoveMode(!batchRemoveMode);
  };

  // 执行批量删除
  const handleBatchDelete = () => {
    const idsToDelete = tableSelectedRowKeys
      .map(k => (typeof k === 'string' ? Number(k) : k))
      .filter(id => !isNaN(Number(id))) as number[];
    deleteConfig(idsToDelete);
    setTableSelectedRowKeys([]);
    setBatchRemoveMode(false);
  };

  const onReset = () => {
    setSearchParams({});
    setPageOffset(1);
  };

  const handleCheckDataPropertyClick = (record: any) => {
    setCurrentDeviceId(record.deviceId);
    setCheckDataPropertyVisible(true);
  };

  const onFinish = (values: any) => {
    const typeId = getChildNodesByParentIds(values.typeId, deviceTypeData?.tree);
    setSearchParams({
      ...values,
      typeId,
    });
    setPageOffset(1);
  };
  const buildColumns = [
    ...columns,
    {
      title: '操作',
      width: 180,
      render: (record: any) => {
        return (
          <Space size={16}>
            <Button type="link" onClick={() => handleCheckDataPropertyClick(record)}>
              监测数据点
            </Button>
            <PopConfirm
              title="确认移除？移除后不可恢复，你还要继续吗？"
              onConfirm={() => deleteConfig(record.deviceId)}
            >
              <Button type="link">移除</Button>
            </PopConfirm>
          </Space>
        );
      },
    },
  ];
  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), { name: '关联抄表设备' }]} className={styles.linkedDevices}>
      <FormContent narrow title="关联抄表设备">
        <Space size="large" style={{ margin: '5px 0' }}>
          {batchRemoveMode ? (
            <>
              <span>
                已选择 <span style={{ color: '#E64242', fontWeight: 500 }}>{tableSelectedRowKeys.length}</span> 项
              </span>
              <PopConfirm title="确认移除？移除后不可恢复，你还要继续吗？" onConfirm={handleBatchDelete}>
                <Button type="primary" disabled={tableSelectedRowKeys.length <= 0}>
                  删除
                </Button>
              </PopConfirm>
              <Button onClick={toggleBatchRemoveMode}>取消</Button>
            </>
          ) : (
            <Space size="small">
              <Button type="primary" onClick={() => setShowModal(true)}>
                <PlusOutlined />
                配置监测设备
              </Button>
              <Button type="primary" onClick={toggleBatchRemoveMode}>
                批量移除
              </Button>
              <Button
                type="primary"
                onClick={() => setStatisticalModalVisible(true)}
                disabled={deviceData?.length === 0}
              >
                批量配置数据点
              </Button>
            </Space>
          )}
          <Space style={{ paddingTop: 20 }}>
            <CustomFilter form={form} onFinish={val => onFinish(val)} onReset={onReset} colSpan={9}>
              <Form.Item name="codeOrName" label="编号/名称" style={{ marginBottom: 0, width: 280 }}>
                <Input
                  placeholder="请输入"
                  // width={200}
                  allowClear
                />
              </Form.Item>
              <Form.Item name="typeId" label="所属类目" style={{ marginBottom: 0, width: 280 }}>
                <Cascader
                  options={formatOptionData(deviceTypeData)}
                  allowClear
                  fieldNames={{ children: 'child' }}
                  multiple
                  maxTagCount="responsive"
                  showSearch={{
                    filter: (inputValue, path) =>
                      path.some(
                        option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1
                      ),
                  }}
                  placeholder={'请选择'}
                />
              </Form.Item>
            </CustomFilter>
          </Space>
        </Space>

        <FieldTable
          rowKey="deviceId"
          dataSource={deviceData}
          columns={buildColumns}
          rowSelection={rowSelection}
          loading={loading}
        />
        <Paging pagingInfo={pagingInfo} />
      </FormContent>
      {/* 配置弹窗 */}
      <UnitModal open={showModal} cancelModal={() => setShowModal(false)} areaId={Number(id)} updateFn={updateFn} />
      {/* 批量配置数据点 */}
      <StatisticalDataModal
        open={statisticalModalVisible}
        areaId={Number(id)}
        onClose={() => setStatisticalModalVisible(false)}
      />
      {/* 监测数据点 */}
      <CheckDataProperty
        areaId={Number(id)}
        open={checkDataPropertyVisible}
        deviceId={currentDeviceId}
        onClose={() => setCheckDataPropertyVisible(false)}
      />
    </Wrapper>
  );
};
export default LinkedMeteringDevices;
